import { Inject, Injectable } from '@angular/core';
import {
	API_BASE_URL_BACKEND,
	DcBaseInitializationService,
	DcCoreBackendBasePath,
} from '@datachain/ui-sdk/common';
import { OrderedMap } from 'immutable';

import { DxListsStorage } from '../core/dx-lists.storage';
import { DcDxGrids } from '../core/dx-types';
import { ProjectStorage } from '../core/project.storage';
import { TagAdapter } from '../core/tag.adapter';
import { IRankingResponse } from '../requesters/ranking/ranking.model';
import { RankingItemEntity } from './components/ranking-list/ranking-item.entity';
import {
	IRankingListExtra,
	RankingListEntity,
} from './components/ranking-list/ranking-list.entity';

@Injectable()
export class RankingAdapter {
	public constructor(
		private readonly tagAdapter: TagAdapter,
		private readonly projectStorage: ProjectStorage,
		private readonly dxListsStorage: DxListsStorage,
		@Inject(API_BASE_URL_BACKEND)
		private readonly apiBase: DcCoreBackendBasePath
	) {}
	public generateRankingList(
		response: ReadonlyArray<IRankingResponse>
	): RankingListEntity {
		const columns = this.getRankingColumns();
		const items = response
			.reduce<Array<RankingItemEntity>>((acc, item) => {
				const temp = this.generateRankingItemEntity(item);
				acc.push(temp);
				return acc;
			}, [])
			.sort((a, b) => {
				if (!a.updateDate) {
					throw new Error('Date is null!');
				}
				if (!b.updateDate) {
					throw new Error('Date is null!');
				}

				return b.updateDate.getTime() - a.updateDate.getTime();
			});
		const orderedMap = OrderedMap<string, RankingItemEntity>(
			items.map((item) => [`${item.id}`, item])
		);
		return columns.setElements(orderedMap);
	}

	private getRankingColumns(): RankingListEntity {
		let availableCols: Array<IRankingListExtra> = [
			{
				field: 'isActive',
				alias: $localize`:i18n=@@general.status:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 0,
				width: '5.5rem',
			},
			{
				field: 'code',
				alias: $localize`:i18n=@@ranking.list.column.code:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 1,
				width: '5.5rem',
			},
			{
				field: 'label',
				alias: $localize`:i18n=@@ranking.list.column.label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 2,
				width: '25%',
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@ranking.list.column.description:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: true,
				visibleIndex: 3,
			},
			{
				field: 'tags',
				alias: $localize`:i18n=@@ranking.list.column.tags:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 4,
			},
			{
				field: 'projectLabel',
				alias: $localize`:i18n=@@ranking.list.column.project:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 5,
			},
			{
				field: 'creationDate',
				alias: $localize`:i18n=@@ranking.list.column.creationDate:`,
				type: 'date',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 6,
				width: '10rem',
			},
			{
				field: 'createdByAvatar',
				alias: $localize`:i18n=@@ranking.list.column.createdBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 7,
			},
			{
				field: 'updateDate',
				alias: $localize`:i18n=@@ranking.list.column.updateDate:`,
				type: 'date',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 8,
				width: '10rem',
			},
			{
				field: 'updatedByAvatar',
				alias: $localize`:i18n=@@ranking.list.column.updatedBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 9,
			},
		];

		availableCols = this.dxListsStorage.synchronize<
			IRankingListExtra,
			RankingItemEntity
		>(DcDxGrids.RankingListStandalone, availableCols);
		return RankingListEntity.build(availableCols);
	}

	private generateRankingItemEntity(
		response: IRankingResponse
	): RankingItemEntity {
		const resourceUrl =
			DcBaseInitializationService.getResource('PICTOGRAMME_IMAGE');
		return RankingItemEntity.build({
			id: response.id,
			label: response.label ?? '',
			code: response.code ?? '',
			description: response.description ?? '',
			projectLabel: response.project?.label ?? '',
			isProjectPublic: response.project?.is_public ?? false,
			updateDate:
				response.update_date === undefined
					? new Date(response.creation_date ?? '')
					: new Date(response.update_date),
			creationDate: response.creation_date
				? new Date(response.creation_date)
				: null,
			updatedBy:
				response.updated_by === undefined
					? response.created_by
						? `${response.created_by.first_name} ${response.created_by.last_name}`
						: ''
					: `${response.updated_by.first_name} ${response.updated_by.last_name}`,
			createdBy: response.created_by
				? `${response.created_by.first_name} ${response.created_by.last_name}`
				: '',
			tags: this.tagAdapter.generateResponseMetadataTag(response.tags ?? []),
			isActive: response.active ?? false,
			pictogramUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.icon_id ?? -1
			}`,
		});
	}
}
